// =============================================================================
// Candidate values for looking a pallet up by the EXTERNALLY-RECEIVED SSCC
// column, `pallets.sscc`.
//
// `pallets` carries TWO DISJOINT SSCC lanes and they are not interchangeable:
//
//   · `ssc_id` — the SSCC WE mint and print (mig 014 / 889 / 495). Every writer
//     runs it through normaliseSscc (and, since mig 20260821000000, through a
//     BEFORE trigger too), and it carries the partial unique index
//     pallets_ssc_id_unique (mig 346). It is therefore always canonical, and a
//     single `.eq('ssc_id', normaliseSscc(input))` is a complete lookup.
//
//   · `sscc` — a THIRD PARTY's label, captured verbatim when their packed
//     pallets are received in for storage (receive_packed_pallets, mig
//     20260807090000; update_received_pallet, mig 20260813090000). Both write
//     it with `btrim()` and NOTHING ELSE — no prefix strip, no AI strip, no
//     uniqueness. That is deliberate: src/lib/sscc.ts records the 2026-08-17
//     decision never to silently rewrite a code someone else issued.
//
// On prod 2026-08-19 the two lanes were set together on ZERO rows.
//
// WHY TWO CANDIDATES, NOT JUST THE NORMALISED ONE.
// normaliseSscc strips the `]C0`/`]C1` AIM symbology prefix, every non-digit,
// and a leading AI `00`. A wedge scan arrives as `]C100006099004423016754` and
// normalises to `006099004423016754`. But because `sscc` holds what was
// CAPTURED, a value written from that same scanner sits in the column still
// wearing its prefix — i.e. in exactly the form normaliseSscc would have
// removed. So matching ONLY the normalised form misses precisely the
// non-canonical rows while working perfectly on the tidy ones: it demos fine
// and ships broken. We match the raw trimmed input as well.
//
// ⚠️ MIGRATION DEPENDENCY — read before simplifying this.
// Mig 20260828061318 widens the pallets normalise trigger to `sscc` and
// rewrites the column in place. It is MERGED BUT UNAPPLIED. Once it has been
// applied, every stored `sscc` is canonical and the raw candidate below is
// redundant — it exists for the PRE-migration rows. Whoever applies
// 20260828061318 is the person allowed to drop it. Until then it stays: a
// search that only works after a pending migration lands is a search that does
// not work.
//
// ⚠️ DO NOT gate a call to this on isValidSscc(). The `ssc_id` paths do, which
// is safe for a column we mint ourselves; `sscc` holds other people's codes,
// and sscc.ts notes a failing check digit is more often a legacy code than a
// typo. Gating on validity would skip the lookup for exactly the rows this
// exists to find.
//
// ⚠️ `sscc` has NO uniqueness constraint, so a lookup on it may legitimately
// return more than one row — use `.limit(n)` and take the first, never
// `.maybeSingle()` (which throws on a second row).
// =============================================================================

import { normaliseSscc } from './sscc'

/**
 * The values a `pallets.sscc` lookup should match for a scanned/typed input,
 * most-canonical first. Empty array when there is nothing worth querying for,
 * in which case the caller should skip the query entirely.
 */
export function ssccMatchCandidates(raw: string | null | undefined): string[] {
  const trimmed = (raw ?? '').trim()
  if (!trimmed) return []
  const normalised = normaliseSscc(trimmed)
  // Normalised first: it is the form every row will hold once mig
  // 20260828061318 applies, and the form the tidy 88 already hold today.
  const out = normalised ? [normalised] : []
  // Raw second, and only when it actually differs — a bare typed SSCC
  // normalises to itself and would otherwise be queried twice.
  if (trimmed !== normalised) out.push(trimmed)
  return out
}
